import { ogImageMeta } from "@/lib/og-images";
import { createFileRoute } from "@tanstack/react-router";
import { LegalLayout, LegalSection } from "@/components/legal-layout";

export const Route = createFileRoute("/refunds")({
  head: () => ({
    meta: [
      { title: "Refund Policy — the Vibing Company®" },
      { name: "description", content: "How refunds and cancellations work for personalized services from the Vibing Company®." },
      { property: "og:title", content: "Refund Policy — the Vibing Company®" },
      ...ogImageMeta("refunds", "Share card: refund policy"),
      { property: "og:description", content: "How refunds and cancellations work for personalized services from the Vibing Company®." },
    ],
    links: [{ rel: "canonical", href: "/refunds" }],
  }),
  component: RefundsPage,
});

function RefundsPage() {
  return (
    <LegalLayout kicker="05.REFUNDS" title="Refund Policy" updated="June 2026">
      <LegalSection label="01" title="Overview">
        <p>
          This Refund Policy explains when refunds are available for engagements with the Vibing
          Company® ("we", "us"). It applies to the personalized services agreed in your proposal
          or engagement agreement. Payments are processed by Stripe.
        </p>
      </LegalSection>

      <LegalSection label="02" title="EU/UK consumer right of withdrawal">
        <p>
          If you are a consumer in the EU or UK, you have a statutory 14-day right of withdrawal
          from the date of purchase. By asking us to begin work before the 14 days expire, you expressly request
          immediate performance and acknowledge that your right of withdrawal lapses once the
          service has been fully performed. For partially performed services, you may be charged
          a proportionate amount for work already done.
        </p>
        <p>
          To withdraw, email{" "}
          <a href="mailto:jon@vibing.inc" className="underline">jon@vibing.inc</a> within
          the 14-day window with your order details.
        </p>
      </LegalSection>

      <LegalSection label="03" title="Ongoing engagements">
        <p>
          Ongoing engagements can be cancelled according to the terms accepted in the proposal or
          agreement. Cancellation stops future renewals where recurring billing was agreed.
        </p>
        <ul className="list-none space-y-2">
          <li>
            — <strong>First 7 days, no work delivered</strong>: full refund of the most recent
            charge.
          </li>
          <li>
            — <strong>Work already delivered in the current period</strong>: no refund for that
            period. Future renewals are cancelled immediately on request.
          </li>
          <li>
            — <strong>Service failure on our side</strong> (we are unable to deliver the agreed
            scope and cannot remedy it within a reasonable time): pro-rata refund for the unused
            portion of the period.
          </li>
        </ul>
        <p>
          Unused capacity, hours or revisions are handled according to the agreed proposal.
        </p>
      </LegalSection>

      <LegalSection label="04" title="Project engagements">
        <p>Refund eligibility for project engagements depends on the stage:</p>
        <ul className="list-none space-y-2">
          <li>
            — <strong>Before work has started</strong> (no intake call, no investigation, no
            code or design work): full refund within 7 days of purchase.
          </li>
          <li>
            — <strong>Work in progress</strong>: partial refund covering the portion of work not
            yet performed, at our reasonable assessment.
          </li>
          <li>
            — <strong>Delivered</strong>: no refund once the deliverable (audit report, fix,
            migration, redesign, etc.) has been handed over.
          </li>
        </ul>
        <p>
          Discovery calls are non-refundable once the call has taken place. If we cancel or
          reschedule and cannot find a suitable alternative within 30 days, the fee is refunded
          in full.
        </p>
      </LegalSection>

      <LegalSection label="05" title="Situations that are not eligible">
        <p>Refunds are not available for:</p>
        <ul className="list-none space-y-2">
          <li>— Change of mind after work has been delivered</li>
           <li>— Unused capacity already reserved under an agreed engagement</li>
          <li>— Third-party costs (hosting, domains, paid APIs) passed through to you</li>
          <li>
            — Delays caused by Client inaction (missing access, content, approvals) after we
            have requested them in writing
          </li>
          <li>
            — Engagements terminated by us under § 12 of the{" "}
            <a href="/terms" className="underline">Terms of Service</a> (acceptable use)
          </li>
        </ul>
      </LegalSection>

      <LegalSection label="06" title="How to request a refund">
        <p>
          Write to{" "}
          <a href="mailto:jon@vibing.inc" className="underline">jon@vibing.inc</a> from the
          email address on the order with:
        </p>
        <ul className="list-none space-y-2">
          <li>— Your order or invoice number (from Stripe)</li>
          <li>— The plan or service in question</li>
          <li>— A short description of why you are requesting a refund</li>
        </ul>
        <p>
          We aim to respond within 3 business days. Approved refunds are issued by Stripe to the
          original payment method and typically appear within 5–10 business days, depending on
          your bank.
        </p>
      </LegalSection>

      <LegalSection label="07" title="Chargebacks">
        <p>
          If you have a concern, please contact us first — we'd much rather solve it than fight a
          chargeback. Disputes opened without prior contact may result in suspension of the
           related engagement and immediate cancellation of any in-flight work.
        </p>
      </LegalSection>

      <LegalSection label="08" title="Statutory rights">
        <p>
          Nothing in this policy limits any non-waivable statutory rights you may have as a
          consumer under German or EU law.
        </p>
      </LegalSection>

      <LegalSection label="09" title="Contact">
        <p>
          Questions:{" "}
          <a href="mailto:jon@vibing.inc" className="underline">jon@vibing.inc</a>.
        </p>
      </LegalSection>
    </LegalLayout>
  );
}
